import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Viewer } from '../lib/webrtc';
import { HeartsOverlay, HeartButton, useHearts } from './LiveHearts';

export default function WebRTCViewer({ liveId }) {
  const videoRef = useRef(null);
  const viewerRef = useRef(null);
  const [status, setStatus] = useState('connecting');
  const [muted, setMuted] = useState(true);
  const { floating, count, pop } = useHearts();

  useEffect(() => {
    const v = new Viewer(liveId, (stream) => {
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {});
      }
    }, setStatus, (ev) => { if (ev.type === 'heart') pop(ev.n); });
    viewerRef.current = v;
    v.connect().catch(() => setStatus('error'));
    return () => v.stop();
  }, [liveId, pop]);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    el.play().catch(() => {});
  };

  const labels = {
    no_broadcaster: 'La vendeuse n\'a pas encore lancé sa caméra. La vidéo démarre automatiquement dès qu\'elle clique « Passer en direct ».',
    waiting_offer: 'Connexion au vendeur…',
    reconnecting: 'Reconnexion…',
    error: 'Connexion impossible. Rechargez la page.',
  };

  return (
    <div className="relative w-full h-full bg-black">
      <video ref={videoRef} data-testid="webrtc-viewer-video" autoPlay playsInline muted
             className="w-full h-full object-contain" />
      {status !== 'receiving' && (
        <div className="absolute inset-0 grid place-items-center text-slate-300 bg-black/70 pointer-events-none">
          <div className="text-center px-6">
            <div className="h-8 w-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <div className="text-sm" data-testid="viewer-status">{labels[status] || 'Connexion en direct…'}</div>
          </div>
        </div>
      )}
      {status === 'receiving' && muted && (
        <button type="button" data-testid="unmute-btn" onClick={toggleSound}
          className="absolute inset-x-0 bottom-16 mx-auto w-max inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 font-bold shadow-2xl animate-pulse">
          <Volume2 className="h-5 w-5" />Activer le son
        </button>
      )}
      {status === 'receiving' && (
        <button type="button" data-testid="mute-toggle" onClick={toggleSound} aria-label="Son"
          className="absolute bottom-3 left-3 h-10 w-10 grid place-items-center rounded-full bg-slate-950/70 text-white backdrop-blur">
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      )}
      <HeartsOverlay floating={floating} />
      <HeartButton count={count} onSend={() => viewerRef.current?.sendHeart()} />
    </div>
  );
}
